/**
 * Product Q&A schemas shared by the engine, the skill and the page (epic
 * PI-SPEC-SW-E310, ER-1667 Package F, contract ask 1).
 *
 * One ask turn travels like this:
 *
 * 1. The page calls `spectreeToolbox.ask.ask` with the product scope, the
 *    question, up to {@link ASK_IMAGES_MAX} screenshots and the person's roles
 *    and persona ({@link askAskInputSchema}).
 * 2. The engine takes the repositories read lock, writes the screenshots and
 *    `turn.json` ({@link askTurnInputSchema}) into the conversation folder
 *    and runs the internal Product Q&A skill with `claude -p`, read-only, with
 *    the product's repository copies passed by absolute path.
 * 3. Answer text streams to the page as `answer-delta` events. The skill ends
 *    with one line, `SPECTREE_ASK_TURN {json}` ({@link askTurnMarkerSchema}),
 *    which the engine strips from the text and narrows with
 *    {@link normalizeAskTurnMarker}: code references and sources that are not
 *    valid are dropped, never refused, and a `spec-found` verdict without a
 *    written-spec source becomes `code-only`.
 * 4. `turn-finished` carries the answer, verdict, code references, sources,
 *    `specAgrees` and an optional redirect to Help.
 *
 * Nothing is stored on the server (Resolved Decision 10d): a conversation
 * lives on the computer until it is closed, and is swept after
 * `ASK_CONVERSATION_TTL_MS`.
 */
import { z } from "zod";
import { absoluteFilePathSchema } from "../formulator/api.js";
import { groomingProjectNameSchema } from "../grooming/api.js";
import { MAX_GROOMING_REPOS } from "../grooming/constants.js";
import { helpImageInputSchema } from "../help/types.js";
import { TOOLBOX_PERSONAS, TOOLBOX_ROLES } from "../roles/types.js";
import { ASK_ANSWER_MAX, ASK_CODE_REF_NOTE_MAX, ASK_CODE_REFS_MAX, ASK_IMAGES_MAX, ASK_MESSAGE_MAX, ASK_MESSAGE_ROLES, ASK_QUESTION_MAX, ASK_REDIRECTS, ASK_RUN_PHASES, ASK_SOURCE_KINDS, ASK_SOURCE_REF_MAX, ASK_SOURCE_TITLE_MAX, ASK_SOURCES_MAX, ASK_SPEC_AGREEMENTS, ASK_STATUS_STATES, ASK_TURN_INPUT_SCHEMA_VERSION, ASK_TURN_MARKER, ASK_TURN_MESSAGES_MAX, ASK_TURN_OUTCOMES, ASK_VERDICTS, isAskCodeRefPath, isAskSpecSourceKind, } from "./constants.js";
import { ASK_ENGINE_ERROR_CODES } from "./errors.js";
// ---------------------------------------------------------------------------
// Scope
// ---------------------------------------------------------------------------
const fogbugzNameSchema = z.string().trim().min(1).max(256);
/** A question about the mapped repositories of one ADO project. */
export const askAdoProjectScopeSchema = z
    .object({
    kind: z.literal("ado-project"),
    project: groomingProjectNameSchema,
})
    .strict();
const fogbugzIdSchema = z.number().int().positive();
/**
 * A question about a FogBugz project (and optional area), answered from the
 * repositories of the ADO project it is linked to (FogBugz ↔ ADO link,
 * ER-1664). `adoProject` is the resolved link target (area override, else
 * the project default). `fogbugzProjectId` (and `fogbugzAreaId` for an area
 * override) let the Toolbox re-check the link before a turn
 * (`PRODUCT_NOT_LINKED` when it no longer points at `adoProject`) and let the
 * skill search the linked FogBugz project's cases with the person's own key.
 */
export const askFogbugzProjectScopeSchema = z
    .object({
    kind: z.literal("fogbugz-project"),
    fogbugzProjectId: fogbugzIdSchema,
    fogbugzProject: fogbugzNameSchema,
    fogbugzAreaId: fogbugzIdSchema.optional(),
    fogbugzArea: fogbugzNameSchema.optional(),
    adoProject: groomingProjectNameSchema,
})
    .strict();
/** What a question is about: an ADO project, or a FogBugz project resolved to one. */
export const askScopeSchema = z.discriminatedUnion("kind", [askAdoProjectScopeSchema, askFogbugzProjectScopeSchema]);
/** The ADO project whose repositories answer questions in `scope`. */
export function askScopeAdoProject(scope) {
    return scope.kind === "ado-project" ? scope.project : scope.adoProject;
}
// ---------------------------------------------------------------------------
// Code references and sources
// ---------------------------------------------------------------------------
/**
 * One "Where in the code" reference: a repository, a path relative to its
 * root ({@link isAskCodeRefPath}: never absolute, never `..`), optional
 * lines and a plain note on what that code does.
 */
export const askCodeRefSchema = z
    .object({
    repo: z.string().trim().min(1).max(256),
    path: z.string().refine(isAskCodeRefPath, { message: "path must be relative to the repository root" }),
    startLine: z.number().int().min(1).optional(),
    endLine: z.number().int().min(1).optional(),
    note: z.string().trim().min(1).max(ASK_CODE_REF_NOTE_MAX),
})
    .refine((ref) => ref.startLine === undefined || ref.endLine === undefined || ref.endLine >= ref.startLine, {
    message: "endLine must not be before startLine",
    path: ["endLine"],
});
/** What an answer relied on: code, a repo doc, an ADO work item or a FogBugz case. */
export const askSourceSchema = z.object({
    kind: z.enum(ASK_SOURCE_KINDS),
    title: z.string().trim().min(1).max(ASK_SOURCE_TITLE_MAX),
    /** A repo doc path, `AB#<id>` or a FogBugz case number. */
    ref: z.string().trim().min(1).max(ASK_SOURCE_REF_MAX),
});
/** Keep only valid code references (at most {@link ASK_CODE_REFS_MAX}); the rest are dropped. */
export function filterAskCodeRefs(markerRefs) {
    const kept = [];
    for (const raw of markerRefs) {
        const parsed = askCodeRefSchema.safeParse(raw);
        if (!parsed.success)
            continue;
        kept.push(parsed.data);
        if (kept.length >= ASK_CODE_REFS_MAX)
            break;
    }
    return kept;
}
/** Keep only valid sources (at most {@link ASK_SOURCES_MAX}); the rest are dropped. */
export function filterAskSources(markerSources) {
    const kept = [];
    for (const raw of markerSources) {
        const parsed = askSourceSchema.safeParse(raw);
        if (!parsed.success)
            continue;
        kept.push(parsed.data);
        if (kept.length >= ASK_SOURCES_MAX)
            break;
    }
    return kept;
}
// ---------------------------------------------------------------------------
// Conversation
// ---------------------------------------------------------------------------
/** One message of a Product Q&A conversation, as `turn.json` and `ask.ask` carry it. */
export const askMessageSchema = z
    .object({
    role: z.enum(ASK_MESSAGE_ROLES),
    text: z.string().max(ASK_MESSAGE_MAX),
})
    .strict();
/** The person's question: 1-{@link ASK_QUESTION_MAX} characters, not blank. */
export const askQuestionSchema = z
    .string()
    .min(1, "question must not be empty")
    .max(ASK_QUESTION_MAX, `question must be at most ${String(ASK_QUESTION_MAX)} characters`)
    .refine((value) => value.trim().length > 0, "question must not be blank");
/**
 * One screenshot as the page hands it to `ask.ask`: Help's image input, so
 * the name, type and size rules are Help's ({@link ASK_IMAGES_MAX} and
 * `ASK_IMAGE_BYTES_MAX` alias Help's limits).
 */
export const askImageInputSchema = helpImageInputSchema;
/** The person's Toolbox roles on the product's project and their chosen persona. */
export const askContextSchema = z
    .object({
    roles: z.array(z.enum(TOOLBOX_ROLES)).max(TOOLBOX_ROLES.length),
    persona: z.enum(TOOLBOX_PERSONAS).optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// Ask (page -> engine)
// ---------------------------------------------------------------------------
/**
 * What `ask.ask` takes. `conversationId` is a UUID the page makes when the
 * panel opens a new conversation and keeps until it is closed. `messages` is
 * the conversation so far as the panel shows it; the engine keeps the last
 * {@link ASK_TURN_MESSAGES_MAX} in `turn.json`.
 */
export const askAskInputSchema = z
    .object({
    conversationId: z.guid("conversationId must be a UUID"),
    scope: askScopeSchema,
    question: askQuestionSchema,
    images: z.array(askImageInputSchema).max(ASK_IMAGES_MAX, `at most ${String(ASK_IMAGES_MAX)} images`),
    context: askContextSchema,
    messages: z.array(askMessageSchema).max(200).optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// turn.json (engine -> skill)
// ---------------------------------------------------------------------------
/** One repository copy the skill may read: its mapped name and its absolute folder. */
export const askTurnRepoSchema = z
    .object({
    repo: z.string().trim().min(1).max(256),
    path: absoluteFilePathSchema,
})
    .strict();
/**
 * `turn.json`: what the engine hands the Product Q&A skill for one turn.
 * Written (mode 0600) in the conversation folder; person text goes here,
 * never on the command line. `repos` are the product's read-only repository
 * copies; `images` are absolute paths of the screenshots the engine wrote
 * next to it, which the skill `Read`s.
 */
export const askTurnInputSchema = z
    .object({
    schemaVersion: z.literal(ASK_TURN_INPUT_SCHEMA_VERSION),
    conversationId: z.guid("conversationId must be a UUID"),
    scope: askScopeSchema,
    repos: z.array(askTurnRepoSchema).min(1).max(MAX_GROOMING_REPOS),
    question: askQuestionSchema,
    messages: z.array(askMessageSchema).max(ASK_TURN_MESSAGES_MAX),
    images: z.array(absoluteFilePathSchema).max(ASK_IMAGES_MAX),
    persona: z.enum(TOOLBOX_PERSONAS).optional(),
    roles: z.array(z.enum(TOOLBOX_ROLES)).max(TOOLBOX_ROLES.length),
})
    .strict();
// ---------------------------------------------------------------------------
// The marker line (skill -> engine)
// ---------------------------------------------------------------------------
/**
 * The JSON after `SPECTREE_ASK_TURN ` on the skill's last line. Only
 * `conversationId` is required to match; the engine refuses a marker for
 * another conversation (`NO_TURN_RETURNED`). `codeRefs` and `sources` are
 * arrays of anything here and are narrowed by {@link normalizeAskTurnMarker};
 * an unknown `verdict` reads as `not-sure`, an unknown `specAgrees` or
 * `redirect` is dropped.
 */
export const askTurnMarkerSchema = z.object({
    conversationId: z.guid("conversationId must be a UUID"),
    verdict: z.enum(ASK_VERDICTS).catch("not-sure"),
    codeRefs: z.array(z.unknown()).catch([]),
    sources: z.array(z.unknown()).catch([]),
    specAgrees: z.enum(ASK_SPEC_AGREEMENTS).optional().catch(undefined),
    redirect: z.enum(ASK_REDIRECTS).optional().catch(undefined),
});
/**
 * Narrow a parsed marker to what the panel may show:
 * - invalid code references (absolute paths, `..` segments, backslashes, ...)
 *   and sources are dropped, and each list is capped;
 * - a `spec-found` verdict with no source of a written-spec kind
 *   (`ASK_SPEC_SOURCE_KINDS`) becomes `code-only`: a spec nobody can point to
 *   was not found;
 * - `specAgrees` is kept only on a `spec-found` answer.
 */
export function normalizeAskTurnMarker(marker) {
    const codeRefs = filterAskCodeRefs(marker.codeRefs);
    const sources = filterAskSources(marker.sources);
    let verdict = marker.verdict;
    if (verdict === "spec-found" && !sources.some((source) => isAskSpecSourceKind(source.kind))) {
        verdict = "code-only";
    }
    const result = { conversationId: marker.conversationId, verdict, codeRefs, sources };
    if (verdict === "spec-found" && marker.specAgrees !== undefined)
        result.specAgrees = marker.specAgrees;
    if (marker.redirect !== undefined)
        result.redirect = marker.redirect;
    return result;
}
/**
 * Find the last `SPECTREE_ASK_TURN {json}` line in `text`, parse it and
 * narrow it. Returns null when there is no valid marker line.
 */
export function parseAskTurnMarkerLine(text) {
    const lines = text.split(/\r?\n/);
    for (let i = lines.length - 1; i >= 0; i -= 1) {
        const line = (lines[i] ?? "").trim();
        if (!line.startsWith(`${ASK_TURN_MARKER} `) && line !== ASK_TURN_MARKER)
            continue;
        let json;
        try {
            json = JSON.parse(line.slice(ASK_TURN_MARKER.length).trim());
        }
        catch {
            return null;
        }
        const parsed = askTurnMarkerSchema.safeParse(json);
        return parsed.success ? normalizeAskTurnMarker(parsed.data) : null;
    }
    return null;
}
/**
 * The answer text with every marker line removed and trailing blank lines
 * trimmed, so the marker never reaches the panel.
 */
export function stripAskTurnMarker(text) {
    return text
        .split(/\r?\n/)
        .filter((line) => !line.trimStart().startsWith(ASK_TURN_MARKER))
        .join("\n")
        .trimEnd();
}
// ---------------------------------------------------------------------------
// Engine status
// ---------------------------------------------------------------------------
/**
 * Readiness of the ask engine. `resetsAt` (ISO 8601) is set for
 * `usage-limit`; `message` is one plain sentence for the person. Lenient, so
 * a newer app may add fields.
 */
export const askStatusSchema = z
    .object({
    state: z.enum(ASK_STATUS_STATES),
    resetsAt: z.string().optional(),
    message: z.string().optional(),
})
    .loose();
// ---------------------------------------------------------------------------
// Events (engine -> page)
// ---------------------------------------------------------------------------
const askErrorCodeSchema = z.enum(ASK_ENGINE_ERROR_CODES);
/** The engine's readiness changed (signed in, usage limit reached or reset, ...). */
export const askStatusChangedEventSchema = z.object({
    type: z.literal("status-changed"),
    status: askStatusSchema,
});
/**
 * The turn moved to a new phase. The page shows the phase's plain phrase
 * (`ASK_RUN_PHASE_LABELS`) until answer text arrives.
 */
export const askTurnProgressEventSchema = z.object({
    type: z.literal("turn-progress"),
    conversationId: z.string(),
    phase: z.enum(ASK_RUN_PHASES),
});
/**
 * More answer text, in order. The page appends `text` to the turn's answer.
 * The marker line is never in it (the engine strips it).
 */
export const askAnswerDeltaEventSchema = z.object({
    type: z.literal("answer-delta"),
    conversationId: z.string(),
    text: z.string(),
});
/**
 * A turn ended. `answered`: `answer` is the whole answer text (marker
 * stripped) with the verdict, code references, sources, `specAgrees` and
 * redirect the engine kept after {@link normalizeAskTurnMarker}. `failed`:
 * `errorCode` and `message` say why; the page keeps the typed question.
 * `stopped`: the person stopped it.
 */
export const askTurnFinishedEventSchema = z.object({
    type: z.literal("turn-finished"),
    conversationId: z.string(),
    outcome: z.enum(ASK_TURN_OUTCOMES),
    verdict: z.enum(ASK_VERDICTS).optional(),
    answer: z.string().max(ASK_ANSWER_MAX).optional(),
    codeRefs: z.array(askCodeRefSchema).max(ASK_CODE_REFS_MAX).default([]),
    sources: z.array(askSourceSchema).max(ASK_SOURCES_MAX).default([]),
    specAgrees: z.enum(ASK_SPEC_AGREEMENTS).optional(),
    redirect: z.enum(ASK_REDIRECTS).optional(),
    errorCode: askErrorCodeSchema.optional(),
    message: z.string().optional(),
});
/**
 * Every event `ask.onEvent` delivers. Pages parse with `safeParse` and ignore
 * events that do not parse: a newer app may send event types this contract
 * does not know.
 */
export const askEventSchema = z.discriminatedUnion("type", [
    askStatusChangedEventSchema,
    askTurnProgressEventSchema,
    askAnswerDeltaEventSchema,
    askTurnFinishedEventSchema,
]);
//# sourceMappingURL=types.js.map